var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { showParticipantInPendingRequests } from '../../../../../backend/utils/shiftStateUtils';
import { ShiftPendingTable } from '../../../../../ui/features/shift-details/components/shift-pending-table';
import { ParticipantsAvailabilityContext } from '../../../../../ui/features/shift-details/contexts/ParticipantsAvailabilityContext';
import { ShiftRosterTableAdmin } from '../../../../../ui/features/shift-roster-table';
import { useShiftsByMissionId } from '../../../../../ui/features/shift/hooks/useShiftsByMissionId';
import { useAddVolunteers } from '../../../../../ui/features/volunteers-wizard-dialog/hooks/useAddVolunteers';
import { useShiftVolunteerManagementWizardSteps } from '../../../../../ui/features/volunteers-wizard-dialog/hooks/useShiftVolunteerManagementWizardSteps';
import { useMissionsTranslation } from '../../../../../ui/hooks';
import { Box } from '@mui/material';
import React from 'react';
export const ShiftDetailsTablesAdmin = ({ shift, participants, missionStatus, missionId, onPromoteShift, onParticipantSelect, onReloadShifts, onReloadParticipants, }) => {
    var _a;
    const { selectPositionStep, selectVolunteersStep } = useShiftVolunteerManagementWizardSteps();
    const { onAddVolunteers } = useAddVolunteers({
        data: {
            shift: {
                id: shift.id,
            },
        },
        steps: [selectPositionStep(), selectVolunteersStep()],
        onAfterSubmit: (participantIds) => __awaiter(void 0, void 0, void 0, function* () {
            yield reloadShiftsAndParticipants();
            setLastModifiedShiftParticipants({
                shiftId: shift.id,
                shiftStartTime: shift.columns.startTime,
                shiftStopTime: shift.columns.stopTime,
                participantIds,
            });
        }),
        onCancel: () => reloadShiftsAndParticipants(),
    });
    const reloadShiftsAndParticipants = () => __awaiter(void 0, void 0, void 0, function* () {
        yield onReloadShifts();
        yield onReloadParticipants();
    });
    const { isParticipantAvailabilityPotentiallyChanged, setLastModifiedShiftParticipants } = React.useContext(ParticipantsAvailabilityContext);
    React.useEffect(() => {
        if (isParticipantAvailabilityPotentiallyChanged({
            shiftId: shift.id,
            shiftStartTime: shift.columns.startTime,
            shiftStopTime: shift.columns.stopTime,
            participantIds: participants.rows.map(({ columns }) => columns.participantId),
        })) {
            onReloadParticipants();
        }
        // Did not include shift id, startTime, stopTime and participants because it caused in useEffect falsely firing twice
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onReloadParticipants, isParticipantAvailabilityPotentiallyChanged]);
    const { t } = useMissionsTranslation();
    const { handleDeny, handleWithdraw, handleApprove, handleSendMessageToVolunteersAction } = useShiftsByMissionId({
        missionId,
    });
    const approvedRequests = participants.rows.filter((participant) => participant.columns.status === 'approved');
    const resolvePositionName = (participation) => {
        const positionCount = participation.columns.positions.length;
        const positionName = positionCount > 1 ? t('shifts.pendingRequest.multiplePositions', { count: positionCount }) : participation.columns.positionName;
        return Object.assign(Object.assign({}, participation), { columns: Object.assign(Object.assign({}, participation.columns), { positionName }) });
    };
    const pendingRequests = participants.rows
        .filter(showParticipantInPendingRequests)
        .map(resolvePositionName)
        .sort(sortParticipantsByPositionsCount);
    const isShiftEnded = shift.columns.stopTime < Date.now();
    function updateLastModifiedShiftParticipants(params) {
        setLastModifiedShiftParticipants({
            shiftId: params.shiftId,
            shiftStartTime: shift.columns.startTime,
            shiftStopTime: shift.columns.stopTime,
            participantIds: params.shiftParticipations.map(({ shiftParticipantId }) => shiftParticipantId),
        });
    }
    return (React.createElement(Box, { display: 'flex', flexDirection: 'column', gap: 6, sx: { p: 2 } },
        React.createElement(ShiftPendingTable, { missionStatus: missionStatus, totalRequestNumber: pendingRequests.length, pendingRequests: pendingRequests, approvedRequests: approvedRequests, shiftPositions: (_a = shift.data.shiftPositions) !== null && _a !== void 0 ? _a : [], reloadShiftsAndParticipants: reloadShiftsAndParticipants, onPromoteShift: onPromoteShift, onHandleApprove: (params) => __awaiter(void 0, void 0, void 0, function* () {
                yield handleApprove(params);
                yield reloadShiftsAndParticipants();
                updateLastModifiedShiftParticipants(params);
            }), onHandleDeny: (params) => __awaiter(void 0, void 0, void 0, function* () {
                yield handleDeny(params);
                yield reloadShiftsAndParticipants();
            }), onRowClick: (row) => onParticipantSelect(row.columns.participantId) }),
        React.createElement(ShiftRosterTableAdmin, { readonly: false, missionStatus: missionStatus, isShiftEnded: isShiftEnded, totalApprovedRequests: approvedRequests.length, numberOfRequiredParticipants: getTotalNumberOfRequiredParticipants(shift), approvedRequests: approvedRequests, onSendMessageToVolunteers: handleSendMessageToVolunteersAction, onPromoteShift: () => __awaiter(void 0, void 0, void 0, function* () {
                yield onPromoteShift();
                yield reloadShiftsAndParticipants();
            }), onHandleWithdraw: (params) => __awaiter(void 0, void 0, void 0, function* () {
                yield handleWithdraw(params);
                yield reloadShiftsAndParticipants();
                updateLastModifiedShiftParticipants(params);
            }), onRowClick: (row) => onParticipantSelect(row.columns.participantId), onAddVolunteer: onAddVolunteers })));
};
function getTotalNumberOfRequiredParticipants(shift) {
    let totalNumberOfRequiredParticipants = 0;
    shift.data.positions.forEach((position) => {
        totalNumberOfRequiredParticipants += position.quantity;
    });
    return totalNumberOfRequiredParticipants;
}
function sortParticipantsByPositionsCount(participantA, participantB) {
    return participantB.columns.positions.length - participantA.columns.positions.length;
}
